import React from 'react';
import uuid from 'uuid/v4';
import classnames from 'classnames';
import DragIcon from './drag-icon';
import { DragItem, dragItemContext, dragSortContext, messages, Config } from './drag-item';
import styles from './drag-sort.scss';
import DragLayer from './drag-layer';
import { useAriaLive } from 'hellospa/common/hooks/use-aria-live';
import Offscreen from '../offscreen';
import { useIntl } from 'react-intl';

/*
  This file exports the public API of drag-sort, starting with the types
  you'll need to know to use it.
*/

/**
 * Array.prototype.splice mutates the original array, so I created this helper
 * to safely move a result. If you're moving data around in formik, use
 * `arrayHelpers.move(result.from, result.to)` instead.
 */
export function moveItem<T>(data: T[], result: DropResult<T>): T[] {
  const clone = [...data];
  const tmp = clone.splice(result.from, 1);
  clone.splice(result.to, 0, ...tmp);
  return clone;
}

export type DropResult<TData> = {
  // These are simply the array index of the items being dropped.
  from: number,
  to: number,
  item: TData,
}

export type DragSortProps<TData, TProps> = {
  onDrop: (e: DropResult<TData>) => void

  // The shape of your data doesn't matter
  data: TData[],

  // This just needs a function that returns the props for your component including a key
  getProps: (t: TData, index: number) => TProps & { key: string },

  component: React.ComponentType<TProps>,

  messageValues: Config<TData>['messageValues'],
}

type DragHandleProps = {
  // You can pass your own children if you don't want to use the default icon.
  children?: React.ReactNode,
  className?: string,
  iconClassName?: string,
  'data-qa-ref': string,
  'hidden'?: boolean,
}


export function DragHandle({
  className,
  'data-qa-ref': dataQaRef,
  hidden,
  iconClassName,
  children = <DragIcon className={classnames(styles.icon, iconClassName)} />,
}: DragHandleProps) {
  const intl = useIntl();
  const config = React.useContext(dragSortContext);
  const { dragRef, grab, item } = React.useContext(dragItemContext);

  if (!dragRef) {
    throw new Error('DragHandle must be rendered inside a DragItem');
  }

  return (
    <div
      ref={dragRef}
      className={classnames(styles.dragHandle, className)}
      style={{
        // Testing Library can read the style for .toBeVisible(), but can't
        // detect if it's invisible using a CSS class.
        visibility: hidden ? 'hidden' : 'visible',
      }}
      data-qa-ref={dataQaRef}
      tabIndex={0}
      onKeyDown={(e) => {
        switch (e.key) {
          case ' ':
            grab(true);
            break;
          default:
        }
      }}
    >
      {children}
      <Offscreen>
        {intl.formatMessage(
          config.messages.dragHandleFocused,
          config.messageValues(item),
        )}
      </Offscreen>
    </div>
  );
}


export function DragSort<TData, TProps>(
  props: DragSortProps<TData, TProps>,
) {
  const { messageValues } = props;
  const announce = useAriaLive();
  const [type] = React.useState(uuid);
  const lastIndex = props.data.length - 1;

  const onDrop = React.useCallback((drop: DropResult<TData>) => {
    announce(messages.itemMoved, {
      ...messageValues(drop.item),
      // drop.to is a 0-indexed position, so it needs to be incremented forthe user
      position: drop.to + 1,
    });
    props.onDrop(drop);
  }, [announce, messageValues, props]);

  const config = React.useMemo(() => ({
    type,
    onDrop,
    lastIndex,
    messages,
    messageValues,
  }), [type, onDrop, lastIndex, messageValues]);

  const children = React.useMemo(() => {
    return props.data.map((t: TData, index: number) => {
      const p = props.getProps(t, index);

      return (
        <DragItem key={p.key} index={index} item={t}>
          <props.component {...p} />
        </DragItem>
      );
    });
  }, [props]);

  return (
    <dragSortContext.Provider value={config}>
      <DragLayer />
      <div>
        {children}
      </div>
    </dragSortContext.Provider>
  );
}
